import { Controller } from "@hotwired/stimulus"

// The modal's message for this key, its %{amount} filled in. The texts come
// from the locale files through the modal's data attributes.
function message(modal, key, amount) {
  const text = modal.dataset[key] || ""
  return amount === undefined ? text : text.replace("%{amount}", Math.round(amount).toLocaleString("fr-FR"))
}

// Handles the partial (advance) payment modal: open/close, amount validation
export default class extends Controller {
  static values = {
    paymentId: String,
    maxAmount: Number,
    minAmount: { type: Number, default: 0 },
  }

  static targets = ["modal", "paymentId", "amountInput", "amountHint", "amountError", "submitBtn", "form"]

  open(event) {
    event.preventDefault()

    // Read values from the trigger button if present
    const btn = event.currentTarget
    if (btn.dataset.partialPaymentPaymentIdValue) {
      this.paymentIdValue = btn.dataset.partialPaymentPaymentIdValue
    }
    if (btn.dataset.partialPaymentMaxAmountValue) {
      this.maxAmountValue = parseFloat(btn.dataset.partialPaymentMaxAmountValue)
    }
    if (btn.dataset.partialPaymentMinAmountValue) {
      this.minAmountValue = parseFloat(btn.dataset.partialPaymentMinAmountValue)
    }

    // Find the global modal (may be outside this controller's element)
    const modal = document.querySelector('[data-partial-payment-target="modal"]')
    if (modal) {
      // The modal validates the amount with ITS OWN values: hand them over,
      // or it compares every amount to 0 and never lets the form go.
      modal.dataset.partialPaymentMaxAmountValue = this.maxAmountValue
      modal.dataset.partialPaymentMinAmountValue = this.minAmountValue
      modal.classList.remove("hidden")

      const paymentIdInput = modal.querySelector('[data-partial-payment-target="paymentId"]')
      if (paymentIdInput) paymentIdInput.value = this.paymentIdValue

      const hint = modal.querySelector('[data-partial-payment-target="amountHint"]')
      if (hint) {
        // The whole remainder is accepted (a full payment): the hint says « up to ».
        const min = this.minAmountValue > 0 ? message(modal, "hintMin", this.minAmountValue) : ""
        const max = message(modal, "hintMax", this.maxAmountValue)
        hint.textContent = [min, max].filter(Boolean).join(" — ")
      }
    }
  }

  close() {
    const modal = document.querySelector('[data-partial-payment-target="modal"]')
    if (modal) modal.classList.add("hidden")
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  validateAmount() {
    const modal = document.querySelector('[data-partial-payment-target="modal"]')
    if (!modal) return

    const input = modal.querySelector('[data-partial-payment-target="amountInput"]')
    const errorEl = modal.querySelector('[data-partial-payment-target="amountError"]')
    const submitBtn = modal.querySelector('[data-partial-payment-target="submitBtn"]')
    if (!input) return

    const amount = parseFloat(input.value)
    let error = ""

    // The whole remainder is a full payment (owner decision 03/10): accepted,
    // even when it is under the minimum advance.
    const wholeRemainder = Math.round(amount) === Math.round(this.maxAmountValue)

    if (isNaN(amount) || amount <= 0) {
      error = message(modal, "errorInvalid")
    } else if (wholeRemainder) {
      error = ""
    } else if (this.minAmountValue > 0 && amount < this.minAmountValue) {
      error = message(modal, "errorMin", this.minAmountValue)
    } else if (amount > this.maxAmountValue) {
      error = message(modal, "errorMax", this.maxAmountValue)
    }

    if (errorEl) {
      errorEl.textContent = error
      errorEl.classList.toggle("hidden", !error)
    }
    if (submitBtn) {
      submitBtn.disabled = !!error || !amount
    }
  }
}
